/**
 * Shared "adapt an overlay icon to the image behind it" logic.
 *
 * An icon (wishlist heart, quick-add "+") that sits on top of product imagery
 * has to stay legible whether the photo behind it is light or dark. This module
 * samples the corner of the image where the icon sits, works out whether that
 * area reads as light or dark (via WCAG contrast, not a naïve brightness cut-off)
 * and toggles a caller-supplied class so the CSS can flip the icon colour.
 *
 * Used by both <wishlist-button> and the cart drawer's quick-add trigger so the
 * two share one, audited implementation.
 */

// WCAG relative luminance (0–1) from 8-bit sRGB channels, including the
// gamma-expansion step — so the light/dark icon decision matches perceived
// contrast rather than a naïve channel average.
function srgbToLinear(channel) {
  const c = channel / 255;
  return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
}

function relativeLuminance(r, g, b) {
  return 0.2126 * srgbToLinear(r) + 0.7152 * srgbToLinear(g) + 0.0722 * srgbToLinear(b);
}

// requestIdleCallback is missing in Safari — fall back to a 0 ms timeout so the
// canvas read still happens off the critical thread.
export const scheduleIdle = typeof requestIdleCallback === 'function'
  ? (cb) => requestIdleCallback(cb, { timeout: 1000 })
  : (cb) => setTimeout(cb, 0);

/**
 * Walk up from the image to the first ancestor with a non-transparent
 * background colour — that's what shows through transparent product PNGs.
 * Falls back to white when nothing opaque is found.
 */
export function resolveBackdrop(el) {
  let node = el;
  while (node && node.nodeType === 1) {
    const match = getComputedStyle(node).backgroundColor.match(/rgba?\(([^)]+)\)/);
    if (match) {
      const parts = match[1].split(',').map((p) => parseFloat(p));
      const alpha = parts.length === 4 ? parts[3] : 1;
      if (alpha > 0) return { r: parts[0], g: parts[1], b: parts[2] };
    }
    node = node.parentElement;
  }
  return { r: 255, g: 255, b: 255 };
}

// Where in the image the icon sits, so we sample the matching corner.
// `sourceEl` is the on-page <img>: when it's laid out with `object-fit: cover`
// the image is cropped to fill its box, so the natural corner isn't what the
// shopper sees under the icon. Resolve the visible slice of the natural image
// first and sample the corner of *that* instead.
function cornerOrigin(corner, naturalWidth, naturalHeight, sample, sourceEl) {
  let visX = 0;
  let visY = 0;
  let visW = naturalWidth;
  let visH = naturalHeight;

  const boxW = sourceEl ? sourceEl.clientWidth : 0;
  const boxH = sourceEl ? sourceEl.clientHeight : 0;
  const fit = sourceEl ? getComputedStyle(sourceEl).objectFit : 'fill';

  if (fit === 'cover' && boxW > 0 && boxH > 0) {
    // cover scales by the larger ratio and centre-crops the overflow
    // (object-position defaults to 50% 50%).
    const scale = Math.max(boxW / naturalWidth, boxH / naturalHeight);
    visW = boxW / scale;
    visH = boxH / scale;
    visX = (naturalWidth - visW) / 2;
    visY = (naturalHeight - visH) / 2;
  }

  const left = visX;
  const top = visY;
  const right = visX + visW - sample;
  const bottom = visY + visH - sample;

  let sx;
  let sy;
  switch (corner) {
    case 'top-left': sx = left; sy = top; break;
    case 'bottom-left': sx = left; sy = bottom; break;
    case 'bottom-right': sx = right; sy = bottom; break;
    case 'top-right':
    default: sx = right; sy = top; break;
  }

  // Keep the sample window inside the image bounds.
  sx = Math.min(Math.max(0, sx), Math.max(0, naturalWidth - sample));
  sy = Math.min(Math.max(0, sy), Math.max(0, naturalHeight - sample));
  return { sx, sy };
}

/**
 * Decide whether the given corner of `img` reads as light (so a dark icon wins
 * on contrast). Transparent pixels are composited over `background` first, so
 * transparent product PNGs contribute the colour the shopper actually sees
 * rather than black. Returns null if the canvas read fails (e.g. tainted).
 */
export function isCornerLight(img, { corner = 'top-right', background = { r: 255, g: 255, b: 255 }, sourceEl } = {}) {
  try {
    const sample = 32;
    const canvas = document.createElement('canvas');
    canvas.width = sample;
    canvas.height = sample;
    const ctx = canvas.getContext('2d', { willReadFrequently: false });
    const { sx, sy } = cornerOrigin(corner, img.naturalWidth, img.naturalHeight, sample, sourceEl);
    ctx.drawImage(img, sx, sy, sample, sample, 0, 0, sample, sample);
    const { data } = ctx.getImageData(0, 0, sample, sample);

    let rTotal = 0;
    let gTotal = 0;
    let bTotal = 0;
    const pixels = data.length / 4;
    for (let i = 0; i < data.length; i += 4) {
      // Composite each pixel over the resolved backdrop so transparent areas
      // contribute the colour the shopper actually sees, not black.
      const alpha = data[i + 3] / 255;
      rTotal += data[i] * alpha + background.r * (1 - alpha);
      gTotal += data[i + 1] * alpha + background.g * (1 - alpha);
      bTotal += data[i + 2] * alpha + background.b * (1 - alpha);
    }

    const luminance = relativeLuminance(rTotal / pixels, gTotal / pixels, bTotal / pixels);
    // Pick whichever icon colour has the higher WCAG contrast against the
    // sampled area instead of a fixed brightness cut-off. The crossover sits at
    // ~0.179 relative luminance, so light-but-not-white backdrops (beige, light
    // grey) correctly get the dark icon.
    const contrastWhite = 1.05 / (luminance + 0.05);
    const contrastBlack = (luminance + 0.05) / 0.05;
    return contrastBlack > contrastWhite;
  } catch (e) {
    // Canvas tainted or other error — let the caller keep the default icon.
    return null;
  }
}

/**
 * Sample the image behind `icon` and toggle `onLightClass` when the relevant
 * corner is light, so the CSS can switch the icon to its dark variant. Loads the
 * pixels through a CORS-enabled Image() so the canvas isn't tainted (Shopify's
 * CDN serves `Access-Control-Allow-Origin: *` on image assets).
 */
export function adaptIconToImage(icon, sourceImg, { corner = 'top-right', onLightClass } = {}) {
  if (!icon || !sourceImg || !onLightClass) return;

  // Product shots are typically transparent PNGs — the colour the shopper
  // actually sees behind the icon is the media background, not the image pixels.
  const background = resolveBackdrop(sourceImg);

  const evaluate = () => {
    const src = sourceImg.currentSrc || sourceImg.src;
    if (!src) return;

    const probe = new Image();
    probe.crossOrigin = 'anonymous';
    probe.decoding = 'async';
    probe.onload = () => {
      // Pass the on-page <img> so cropping (object-fit: cover) is accounted for
      // and we sample the corner the shopper actually sees behind the icon.
      const light = isCornerLight(probe, { corner, background, sourceEl: sourceImg });
      if (light !== null) icon.classList.toggle(onLightClass, light);
    };
    probe.onerror = () => { /* leave the default icon colour */ };
    probe.src = src;
  };

  if (sourceImg.complete && sourceImg.naturalWidth > 0) {
    evaluate();
  } else {
    sourceImg.addEventListener('load', evaluate, { once: true });
  }
}
